import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LanIcon from "@mui/icons-material/Lan";
import { useAuth } from "../contexts/AuthContext";
import ConnectionSettings from "../components/ConnectionSettings";

export default function LoginPage() {
  const theme = useTheme();
  const { login, loading, error } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [netOpen, setNetOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    await login(username.trim(), password);
  };

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 400,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, isDark ? 1 : 0.92),
          boxShadow: isDark
            ? `0 12px 30px ${alpha(theme.palette.common.black, 0.28)}`
            : `0 10px 26px ${alpha(theme.palette.common.black, 0.07)}`,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: isDark
                ? alpha("#a6b078", 0.18)
                : alpha("#556b2f", 0.1),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <LockOutlinedIcon
              sx={{
                fontSize: 26,
                color: isDark ? "#a6b078" : "#556b2f",
              }}
            />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 22, letterSpacing: 0.3 }}>
            CHACAL
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Fire Mission Calculator
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            sx={{ mb: 3 }}
            size="small"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !username.trim() || !password}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              py: 1.1,
              bgcolor: isDark ? "#a6b078" : "#556b2f",
              color: isDark ? "#11150f" : "#f7f7f2",
              "&:hover": {
                bgcolor: isDark ? "#b7c58a" : "#465a27",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "inherit" }} />
            ) : (
              "Sign In"
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            size="small"
            startIcon={<LanIcon sx={{ fontSize: 16 }} />}
            onClick={() => setNetOpen(true)}
            sx={{ textTransform: "none", color: "text.secondary", fontWeight: 600 }}
          >
            Connection settings
          </Button>
        </Box>
      </Paper>
      <ConnectionSettings open={netOpen} onClose={() => setNetOpen(false)} />
    </Box>
  );
}
